import { useQuery } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  useNavigate,
  useParams,
  type RouterHistory,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import type { AdminApi } from '../api/client.ts';
import { environmentsQuery, meQuery } from '../api/queries.ts';
import { Can, RequirePermission } from '../features/access/Can.tsx';
import { EnvironmentBadge } from '@bb/ui';
import { EnvironmentSwitcher } from '../components/EnvironmentSwitcher.tsx';
import { ChangesBadge } from '../features/history/ChangesBadge.tsx';
import { CommandPalette } from '../features/search/CommandPalette.tsx';
import { ObjectTabs } from '../features/tabs/ObjectTabs.tsx';
import { ApiPage } from './ApiPage.tsx';
import { AppDetailPage } from './AppDetailPage.tsx';
import { AppsPage } from './AppsPage.tsx';
import { AuditPage } from './AuditPage.tsx';
import { InternalGroupsPage } from './InternalGroupsPage.tsx';
import { TagsPage } from './TagsPage.tsx';
import { RetentionPage } from './RetentionPage.tsx';
import { SubscriptionGroupPage } from './SubscriptionGroupPage.tsx';
import { SubscriptionGroupsPage } from './SubscriptionGroupsPage.tsx';
import { MediaLibraryPage } from '../features/media/MediaLibraryPage.tsx';
import { NewTemplateEditor, TemplateEditor } from '../features/templates/TemplateEditor.tsx';
import { TemplatesPage } from '../features/templates/TemplatesPage.tsx';
import { ContentBlockEditor, NewContentBlockEditor } from '../features/content-blocks/ContentBlockEditor.tsx';
import { ContentBlocksPage } from '../features/content-blocks/ContentBlocksPage.tsx';
import { TEMPLATE_CHANNELS } from '../features/templates/templateContent.ts';
import type { TemplateChannel } from '../api/client.ts';
import { EventLogPage } from '../features/debug/EventLogPage.tsx';
import { GlobalControlPage } from '../features/exclusions/GlobalControlPage.tsx';
import { ExportsPage } from '../features/exports/ExportsPage.tsx';
import { SuppressionListPage } from '../features/exclusions/SuppressionListPage.tsx';
import { SuppressionListsPage } from '../features/exclusions/SuppressionListsPage.tsx';
import { SdkDebuggerPage } from '../features/debug/SdkDebuggerPage.tsx';
import { SegmentEditorPage } from '../features/segments/SegmentEditorPage.tsx';
import { SegmentInsightsPage } from '../features/segments/SegmentInsightsPage.tsx';
import { SegmentsListPage } from '../features/segments/SegmentsListPage.tsx';
import { CampaignPage } from '../features/campaigns/CampaignPage.tsx';
import { CampaignAnalyticsPage } from '../features/campaigns/analytics/CampaignAnalyticsPage.tsx';
import { campaignAnalyticsSearch } from '../features/campaigns/analytics/analytics.ts';
import { ExtrasSchemaPage } from '../features/settings/ExtrasSchemaPage.tsx';
import { EnvironmentLocalesPage } from '../features/settings/EnvironmentLocalesPage.tsx';
import { DeeplinksPage } from '../features/links/DeeplinksPage.tsx';
import { ApprovalSettingsPage } from '../features/settings/ApprovalSettingsPage.tsx';
import { FrequencyCapsPage } from '../features/settings/FrequencyCapsPage.tsx';
import { RateLimitsPage } from '../features/settings/RateLimitsPage.tsx';
import { PushSettingsPage } from '../features/settings/push/PushSettingsPage.tsx';
import { ApprovalInboxPage } from '../features/approvals/ApprovalInboxPage.tsx';
import { CanvasListPage } from '../features/canvas/CanvasListPage.tsx';
import { CalendarPage } from '../features/calendar/CalendarPage.tsx';
import { calendarSearch } from '../features/calendar/dates.ts';
import { CanvasPage } from '../features/canvas/CanvasPage.tsx';
import { HomePage } from '../features/home/HomePage.tsx';
import { ImportPage } from '../features/imports/ImportPage.tsx';
import { ImportsPage } from '../features/imports/ImportsPage.tsx';
import { PrivacyPage } from '../features/privacy/PrivacyPage.tsx';
import { MergeProfilesPage } from '../features/profiles/MergeProfilesPage.tsx';
import { BrandThemesPage } from '../features/settings/BrandThemesPage.tsx';
import { EventExportPage } from '../features/settings/EventExportPage.tsx';
import { WebhookEndpointsPage } from '../features/settings/WebhookEndpointsPage.tsx';
import { ChannelDashboardPage } from '../features/dashboards/ChannelDashboardPage.tsx';
import { CanvasEditorPage } from '../features/canvas/editor/CanvasEditor.tsx';
import { CanvasAnalyticsPage, canvasAnalyticsSearch } from '../features/canvas/analytics/CanvasAnalyticsPage.tsx';
import { PushAnalyticsPage } from '../features/analytics/push/PushAnalyticsPage.tsx';
import { ActivityLogPage } from '../features/analytics/activity/ActivityLogPage.tsx';
import { periodSearch } from '../features/analytics/period.ts';
import { CampaignsListPage } from '../features/campaigns/CampaignsListPage.tsx';
import { AttributePage } from './AttributePage.tsx';
import { TrustedDataPage } from './TrustedDataPage.tsx';
import { AliasesPage } from './AliasesPage.tsx';
import { AttributesPage } from './AttributesPage.tsx';
import { EventPage } from './EventPage.tsx';
import { EventsPage } from './EventsPage.tsx';
import { LoginPage } from './LoginPage.tsx';
import { PublicPreviewPage } from './PublicPreviewPage.tsx';
import { UsersPage } from './UsersPage.tsx';

export interface RouterContext {
  api: AdminApi;
  queryClient: QueryClient;
}

export { environmentsQuery, meQuery };

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: () => <Outlet /> });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async ({ context }) => {
    const environments = await context.queryClient.ensureQueryData(environmentsQuery(context.api));
    const first = environments[0];
    if (first)
      throw redirect({ to: '/env/$environmentId/home', params: { environmentId: first.id } });
  },
  component: () => <p className="empty-state">No environments are available to your account yet.</p>,
});

function EnvironmentLayout() {
  const { environmentId } = useParams({ from: '/env/$environmentId' });
  const { api } = envRoute.useRouteContext();
  const navigate = useNavigate();
  const { data: environments = [], isError, isPending } = useQuery(environmentsQuery(api));
  const { data: me } = useQuery(meQuery(api));
  const current = environments.find((env) => env.id === environmentId);
  const signOut = async () => {
    await api.logout().catch(() => undefined);
    await navigate({ to: '/login', search: {} });
  };

  return (
    <div className="shell">
      <header className="shell__header">
        <Link to="/" className="shell__brand">
          Braze Brother
        </Link>
        {current && <EnvironmentBadge kind={current.kind} />}
        {current && <ChangesBadge environmentId={current.id} objectType="environment" objectId={current.id} label={current.name} />}
        {isPending ? (
          <span aria-busy="true">Loading environments…</span>
        ) : (
          <EnvironmentSwitcher
            environments={environments}
            currentId={environmentId}
            onChange={(nextId) =>
              // Switching environment never carries page filters across: they belong to the old data set.
              void navigate({
                to: '/env/$environmentId/home',
                params: { environmentId: nextId },
                search: {},
              })
            }
          />
        )}
        <CommandPalette environmentId={environmentId} />
        {me && <span className="shell__user">{me.employee.email}</span>}
        <button type="button" className="button" onClick={() => void signOut()}>
          Sign out
        </button>
      </header>
      <nav className="shell__nav" aria-label="Settings">
        <Link to="/env/$environmentId/home" params={{ environmentId }}>
          Home
        </Link>
        <Link to="/env/$environmentId/dashboards/$channel" params={{ environmentId, channel: 'push' }}>
          Channel dashboards
        </Link>
        <Link to="/env/$environmentId/analytics/push" params={{ environmentId }} search={{}}>
          Push analytics
        </Link>
        <Link to="/env/$environmentId/analytics/activity-log" params={{ environmentId }}>
          Message activity
        </Link>
        <Link to="/env/$environmentId/settings/apps" params={{ environmentId }}>
          Apps
        </Link>
        <Link to="/env/$environmentId/settings/push" params={{ environmentId }}>
          Push Settings
        </Link>
        <Link to="/env/$environmentId/settings/api" params={{ environmentId }}>
          APIs and Identifiers
        </Link>
        <Link to="/env/$environmentId/settings/internal-groups" params={{ environmentId }}>
          Internal Groups
        </Link>
        <Link to="/env/$environmentId/settings/tags" params={{ environmentId }}>
          Tags
        </Link>
        <Link to="/env/$environmentId/campaigns" params={{ environmentId }}>
          Campaigns
        </Link>
        <Link to="/env/$environmentId/canvas" params={{ environmentId }}>
          Canvas
        </Link>
        <Link to="/env/$environmentId/calendar" params={{ environmentId }}>
          Content Calendar
        </Link>
        <Link to="/env/$environmentId/approvals" params={{ environmentId }}>
          Approvals
        </Link>
        <Link to="/env/$environmentId/audience/segments" params={{ environmentId }}>
          Segments
        </Link>
        <Can permission="profiles.view">
          <Link to="/env/$environmentId/audience/import" params={{ environmentId }}>
            Import users
          </Link>
          <Link to="/env/$environmentId/audience/merge" params={{ environmentId }}>
            Merge users
          </Link>
        </Can>
        <Link to="/env/$environmentId/audience/subscription-groups" params={{ environmentId }}>
          Subscription Groups
        </Link>
        <Link to="/env/$environmentId/data/events" params={{ environmentId }}>
          Custom Events
        </Link>
        <Link to="/env/$environmentId/data/attributes" params={{ environmentId }}>
          Custom Attributes
        </Link>
        <Link to="/env/$environmentId/data/trust" params={{ environmentId }}>
          Trusted Data
        </Link>
        <Link to="/env/$environmentId/data/aliases" params={{ environmentId }}>
          Name Aliases
        </Link>
        <Link to="/env/$environmentId/content/media" params={{ environmentId }} search={{}}>
          Media Library
        </Link>
        <Link to="/env/$environmentId/content/templates" params={{ environmentId }}>
          Templates
        </Link>
        <Link to="/env/$environmentId/content/blocks" params={{ environmentId }}>
          Content blocks
        </Link>
        <Link to="/env/$environmentId/settings/extras" params={{ environmentId }}>
          Message extras
        </Link>
        <Link to="/env/$environmentId/settings/locales" params={{ environmentId }}>
          Locales
        </Link>
        <Link to="/env/$environmentId/settings/brand" params={{ environmentId }}>
          Brand
        </Link>
        <Link to="/env/$environmentId/settings/deeplinks" params={{ environmentId }}>
          Deep links
        </Link>
        <Link to="/env/$environmentId/settings/approvals" params={{ environmentId }}>
          Approval settings
        </Link>
        <Link to="/env/$environmentId/settings/suppression-lists" params={{ environmentId }}>
          Suppression Lists
        </Link>
        <Link to="/env/$environmentId/settings/global-control" params={{ environmentId }}>
          Global Control Group
        </Link>
        <Link to="/env/$environmentId/settings/frequency-caps" params={{ environmentId }}>
          Frequency Capping Rules
        </Link>
        <Link to="/env/$environmentId/settings/rate-limits" params={{ environmentId }}>
          Messaging Rate Limits
        </Link>
        <Can permission="data.export">
          <Link to="/env/$environmentId/settings/exports" params={{ environmentId }}>
            My exports
          </Link>
        </Can>
        <Link to="/env/$environmentId/settings/retention" params={{ environmentId }}>
          Data retention
        </Link>
        <Can permission="profiles.view">
          <Link to="/env/$environmentId/settings/event-log" params={{ environmentId }}>
            Event User Log
          </Link>
          <Link to="/env/$environmentId/settings/sdk-debugger" params={{ environmentId }}>
            SDK Debugger
          </Link>
          <Link to="/env/$environmentId/settings/privacy" params={{ environmentId }}>
            Privacy requests
          </Link>
        </Can>
        <Can permission="integrations.manage">
          <Link to="/env/$environmentId/settings/event-export" params={{ environmentId }}>
            Event export
          </Link>
          <Link to="/env/$environmentId/settings/webhooks" params={{ environmentId }}>
            Webhooks
          </Link>
        </Can>
        <Can permission="employees.manage" scope="organization">
          <Link to="/env/$environmentId/settings/users" params={{ environmentId }}>
            Users
          </Link>
        </Can>
        <Can permission="audit.view" scope="organization">
          <Link to="/env/$environmentId/settings/audit" params={{ environmentId }}>
            Audit log
          </Link>
        </Can>
      </nav>
      <ObjectTabs environmentId={environmentId} />
      <main className="shell__main">
        {isError ? <p role="alert">Environments could not be loaded.</p> : <Outlet />}
      </main>
    </div>
  );
}

const envRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/env/$environmentId',
  component: EnvironmentLayout,
});

const appsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/apps',
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search.q === 'string' && search.q ? { q: search.q } : {},
  component: () => (
    <RequirePermission permission="env.view">
      <AppsPage />
    </RequirePermission>
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/users',
  component: () => (
    <RequirePermission permission="employees.manage" scope="organization">
      <UsersPage />
    </RequirePermission>
  ),
});

const appDetailRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/apps/$appId',
  component: () => (
    <RequirePermission permission="env.view">
      <AppDetailPage />
    </RequirePermission>
  ),
});

const apiRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/api',
  component: () => (
    <RequirePermission permission="env.view">
      <ApiPage />
    </RequirePermission>
  ),
});

const internalGroupsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/internal-groups',
  component: () => (
    <RequirePermission permission="env.view">
      <InternalGroupsPage />
    </RequirePermission>
  ),
});

const tagsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/tags',
  component: () => (
    <RequirePermission permission="env.view">
      <TagsPage />
    </RequirePermission>
  ),
});

const retentionRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/retention',
  component: () => (
    <RequirePermission permission="env.view">
      <RetentionPage />
    </RequirePermission>
  ),
});

const suppressionListsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/suppression-lists',
  component: () => (
    <RequirePermission permission="env.view">
      <SuppressionListsPage />
    </RequirePermission>
  ),
});

const suppressionListRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/suppression-lists/$listId',
  component: () => (
    <RequirePermission permission="env.view">
      <SuppressionListPage />
    </RequirePermission>
  ),
});

const globalControlRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/global-control',
  component: () => (
    <RequirePermission permission="env.view">
      <GlobalControlPage />
    </RequirePermission>
  ),
});

const frequencyCapsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/frequency-caps',
  component: () => (
    <RequirePermission permission="env.view">
      <FrequencyCapsPage />
    </RequirePermission>
  ),
});

const pushSettingsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/push',
  component: () => (
    <RequirePermission permission="env.view">
      <PushSettingsPage />
    </RequirePermission>
  ),
});

const rateLimitsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/rate-limits',
  component: () => (
    <RequirePermission permission="env.view">
      <RateLimitsPage />
    </RequirePermission>
  ),
});

const exportsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/exports',
  component: () => (
    <RequirePermission permission="data.export">
      <ExportsPage />
    </RequirePermission>
  ),
});

const eventLogRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/event-log',
  component: () => (
    <RequirePermission permission="profiles.view">
      <EventLogPage />
    </RequirePermission>
  ),
});

const sdkDebuggerRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/sdk-debugger',
  component: () => (
    <RequirePermission permission="profiles.view">
      <SdkDebuggerPage />
    </RequirePermission>
  ),
});

const extrasRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/extras',
  component: () => (
    <RequirePermission permission="env.view">
      <ExtrasSchemaPage />
    </RequirePermission>
  ),
});

const localesRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/locales',
  component: () => (
    <RequirePermission permission="env.view">
      <EnvironmentLocalesPage />
    </RequirePermission>
  ),
});

const deeplinksRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/deeplinks',
  component: () => (
    <RequirePermission permission="env.view">
      <DeeplinksPage />
    </RequirePermission>
  ),
});

const approvalsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/approvals',
  component: () => (
    <RequirePermission permission="env.view">
      <ApprovalInboxPage />
    </RequirePermission>
  ),
});

const approvalSettingsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/approvals',
  component: () => (
    <RequirePermission permission="env.view">
      <ApprovalSettingsPage />
    </RequirePermission>
  ),
});

const homeRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/home',
  component: () => (
    <RequirePermission permission="reports.view">
      <HomePage />
    </RequirePermission>
  ),
});

const dashboardsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/dashboards/$channel',
  component: () => (
    <RequirePermission permission="reports.view">
      <ChannelDashboardPage />
    </RequirePermission>
  ),
});

const pushAnalyticsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/analytics/push',
  validateSearch: periodSearch,
  component: () => (
    <RequirePermission permission="reports.view">
      <PushAnalyticsPage />
    </RequirePermission>
  ),
});

const activityLogRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/analytics/activity-log',
  component: () => (
    <RequirePermission permission="reports.view">
      <ActivityLogPage />
    </RequirePermission>
  ),
});

const importsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/audience/import',
  component: () => (
    <RequirePermission permission="profiles.view">
      <ImportsPage />
    </RequirePermission>
  ),
});

const importRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/audience/import/$importId',
  component: () => (
    <RequirePermission permission="profiles.view">
      <ImportPage />
    </RequirePermission>
  ),
});

const privacyRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/privacy',
  component: () => (
    <RequirePermission permission="profiles.view">
      <PrivacyPage />
    </RequirePermission>
  ),
});

const mergeProfilesRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/audience/merge',
  component: () => (
    <RequirePermission permission="profiles.view">
      <MergeProfilesPage />
    </RequirePermission>
  ),
});

const brandRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/brand',
  component: () => (
    <RequirePermission permission="env.view">
      <BrandThemesPage />
    </RequirePermission>
  ),
});

const eventExportRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/event-export',
  component: () => (
    <RequirePermission permission="integrations.manage">
      <EventExportPage />
    </RequirePermission>
  ),
});

const webhooksRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/webhooks',
  component: () => (
    <RequirePermission permission="integrations.manage">
      <WebhookEndpointsPage />
    </RequirePermission>
  ),
});

const calendarRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/calendar',
  validateSearch: calendarSearch,
  component: () => (
    <RequirePermission permission="env.view">
      <CalendarPage />
    </RequirePermission>
  ),
});

const canvasListRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/canvas',
  component: () => (
    <RequirePermission permission="env.view">
      <CanvasListPage />
    </RequirePermission>
  ),
});

const canvasEditorRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/canvas/$canvasId/edit',
  component: () => (
    <RequirePermission permission="env.view">
      <CanvasEditorPage />
    </RequirePermission>
  ),
});

const canvasAnalyticsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/canvas/$canvasId/analytics',
  validateSearch: canvasAnalyticsSearch,
  component: () => (
    <RequirePermission permission="reports.view">
      <CanvasAnalyticsPage />
    </RequirePermission>
  ),
});

const canvasRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/canvas/$canvasId',
  component: () => (
    <RequirePermission permission="env.view">
      <CanvasPage />
    </RequirePermission>
  ),
});

const campaignsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/campaigns',
  component: () => (
    <RequirePermission permission="env.view">
      <CampaignsListPage />
    </RequirePermission>
  ),
});

const campaignAnalyticsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/campaigns/$campaignId/analytics',
  validateSearch: campaignAnalyticsSearch,
  component: () => (
    <RequirePermission permission="reports.view">
      <CampaignAnalyticsPage />
    </RequirePermission>
  ),
});

const campaignRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/campaigns/$campaignId',
  component: () => (
    <RequirePermission permission="env.view">
      <CampaignPage />
    </RequirePermission>
  ),
});

const segmentsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/audience/segments',
  component: () => (
    <RequirePermission permission="env.view">
      <SegmentsListPage />
    </RequirePermission>
  ),
});

const segmentInsightsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/audience/segment-insights',
  component: () => (
    <RequirePermission permission="env.view">
      <SegmentInsightsPage />
    </RequirePermission>
  ),
});

const segmentRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/audience/segments/$segmentId',
  component: () => (
    <RequirePermission permission="env.view">
      <SegmentEditorPage />
    </RequirePermission>
  ),
});

const subscriptionGroupsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/audience/subscription-groups',
  component: () => (
    <RequirePermission permission="profiles.view">
      <SubscriptionGroupsPage />
    </RequirePermission>
  ),
});

const subscriptionGroupRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/audience/subscription-groups/$groupId',
  component: () => (
    <RequirePermission permission="profiles.view">
      <SubscriptionGroupPage />
    </RequirePermission>
  ),
});

const eventsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/data/events',
  component: () => (
    <RequirePermission permission="profiles.view">
      <EventsPage />
    </RequirePermission>
  ),
});

const eventRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/data/events/$name',
  component: () => (
    <RequirePermission permission="profiles.view">
      <EventPage />
    </RequirePermission>
  ),
});

const attributesRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/data/attributes',
  component: () => (
    <RequirePermission permission="profiles.view">
      <AttributesPage />
    </RequirePermission>
  ),
});

const attributeRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/data/attributes/$key',
  component: () => (
    <RequirePermission permission="profiles.view">
      <AttributePage />
    </RequirePermission>
  ),
});

const aliasesRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/data/aliases',
  component: () => (
    <RequirePermission permission="profiles.view">
      <AliasesPage />
    </RequirePermission>
  ),
});

const trustRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/data/trust',
  component: () => (
    <RequirePermission permission="profiles.view">
      <TrustedDataPage />
    </RequirePermission>
  ),
});

const mediaRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/content/media',
  validateSearch: (search: Record<string, unknown>): { asset?: string } => (typeof search.asset === 'string' && search.asset ? { asset: search.asset } : {}),
  component: () => (
    <RequirePermission permission="env.view">
      <MediaLibraryPage />
    </RequirePermission>
  ),
});

const templatesRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/content/templates',
  component: () => (
    <RequirePermission permission="env.view">
      <TemplatesPage />
    </RequirePermission>
  ),
});

const newTemplateRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/content/templates/new',
  validateSearch: (search: Record<string, unknown>): { channel: TemplateChannel } => ({ channel: (TEMPLATE_CHANNELS as readonly unknown[]).includes(search.channel) ? (search.channel as TemplateChannel) : 'push' }),
  component: function NewTemplateRoute() {
    const { environmentId } = newTemplateRoute.useParams();
    const { channel } = newTemplateRoute.useSearch();
    return (
      <RequirePermission permission="content.edit">
        <NewTemplateEditor key={channel} environmentId={environmentId} channel={channel} />
      </RequirePermission>
    );
  },
});

const templateRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/content/templates/$templateId',
  component: function TemplateRoute() {
    const { environmentId, templateId } = templateRoute.useParams();
    return (
      <RequirePermission permission="env.view">
        <TemplateEditor environmentId={environmentId} templateId={templateId} />
      </RequirePermission>
    );
  },
});

const contentBlocksRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/content/blocks',
  component: () => (
    <RequirePermission permission="env.view">
      <ContentBlocksPage />
    </RequirePermission>
  ),
});

const newContentBlockRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/content/blocks/new',
  component: function NewContentBlockRoute() {
    const { environmentId } = newContentBlockRoute.useParams();
    return (
      <RequirePermission permission="content.edit">
        <NewContentBlockEditor environmentId={environmentId} />
      </RequirePermission>
    );
  },
});

const contentBlockRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/content/blocks/$blockId',
  component: function ContentBlockRoute() {
    const { environmentId, blockId } = contentBlockRoute.useParams();
    return (
      <RequirePermission permission="env.view">
        <ContentBlockEditor environmentId={environmentId} blockId={blockId} />
      </RequirePermission>
    );
  },
});

const auditRoute = createRoute({
  getParentRoute: () => envRoute,
  path: '/settings/audit',
  component: () => (
    <RequirePermission permission="audit.view" scope="organization">
      <AuditPage />
    </RequirePermission>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { error?: string; returnTo?: string } => ({
    ...(typeof search.error === 'string' ? { error: search.error } : {}),
    ...(typeof search.returnTo === 'string' && search.returnTo.startsWith('/')
      ? { returnTo: search.returnTo }
      : {}),
  }),
  component: LoginPage,
});

// A preview link (BB-59): no sign-in, outside the environment layout.
const publicPreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/preview/$token',
  component: function PublicPreviewRoute() {
    const { token } = publicPreviewRoute.useParams();
    return <PublicPreviewPage token={token} />;
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  publicPreviewRoute,
  envRoute.addChildren([homeRoute, dashboardsRoute, pushAnalyticsRoute, activityLogRoute, importsRoute, importRoute, privacyRoute, mergeProfilesRoute, brandRoute, eventExportRoute, webhooksRoute, appsRoute, appDetailRoute, apiRoute, internalGroupsRoute, tagsRoute, retentionRoute, suppressionListsRoute, suppressionListRoute, globalControlRoute, frequencyCapsRoute, rateLimitsRoute, pushSettingsRoute, exportsRoute, eventLogRoute, sdkDebuggerRoute, extrasRoute, localesRoute, deeplinksRoute, approvalsRoute, approvalSettingsRoute, calendarRoute, canvasListRoute, canvasEditorRoute, canvasAnalyticsRoute, canvasRoute, campaignsRoute, campaignAnalyticsRoute, campaignRoute, segmentsRoute, segmentRoute, segmentInsightsRoute, subscriptionGroupsRoute, subscriptionGroupRoute, eventsRoute, eventRoute, attributesRoute, attributeRoute, trustRoute, aliasesRoute, mediaRoute, templatesRoute, newTemplateRoute, templateRoute, contentBlocksRoute, newContentBlockRoute, contentBlockRoute, usersRoute, auditRoute]),
]);

export function createAppRouter(context: RouterContext, history?: RouterHistory) {
  return createRouter({ routeTree, context, ...(history ? { history } : {}), defaultPreload: 'intent' });
}

export { appsRoute };

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
